import { FileAttachment } from 'api/attachments';
import { Comment as Comment$ } from 'api/document/types/document';
import { ExternalUser } from 'api/integrations';
import { User } from 'api/models';
import { ProjectGlobalConversation } from 'api/project-conversations/global-conversation';
import { FieldOption, TaskType } from 'api/tasks';

export enum ContextType {
  TASK = 'tasks',
  DOCUMENT = 'documents',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  SPACE_TAB = 'space-tabs',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  PROJECT_CONVERSATIONS = 'project-conversations',
  ATTACHMENTS = 'attachments',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  RESOURCE_REQUEST = 'resource-request',
}
export enum Reaction {
  ThumbUp = '👍',
  Heart = '❤️',
  Clap = '👏',
}

export type ReactionJSONProps = {
  reactionType: Reaction;
};

export interface ReactionProps extends ReactionJSONProps {
  users: User[];
}

export type Task = {
  taskId: number;
  taskName: string;
  commentThreadId: number;
  createdBy: number;
  createdAt: number;
};

export type Document = {
  documentId: number;
  content: string | null;
  accountId: number;
  projectId: number;
  createdBy: number;
  version: number;
  updatedAt: number | null;
  name: string;
  versioningQueued: string | null;
};

export interface BaseComment {
  commentId: number;
  commentThreadId?: number;
  createdAt: number;
  commentGuid: string;
  private: boolean;
  user: User;
  commentContext?: {
    id: number;
  };
}

export enum WebSocketConversationCommentContextType {
  Task = 'TASK',
  Document = 'DOCUMENT',
  ProjectConversation = 'PROJECT_CONVERSATIONS',
  Attachments = 'ATTACHMENTS',
  SpaceTab = 'SPACE_TAB',
  ResourceRequest = 'RESOURCE_REQUEST',
}

export type WebsocketStatusUpdateConversationContext = {
  commentContext: {
    id: number;
    parentType: 'STATUS_UPDATE';
    type: WebSocketConversationCommentContextType.Document;
    associatedDocument: {
      documentId: number;
      accountId: number;
      projectId: number;
      title: string;
      commentThreadId: number;
      statusUpdate: {
        statusUpdateId: number;
      };
    };
  };
};

export type WebsocketExternalDocumentConversationContext = {
  commentContext: {
    id: number;
    parentTask: never;
    type: WebSocketConversationCommentContextType.SpaceTab;
    associatedSpaceTab: {
      metaData: {
        icon: {
          href: string;
          type: 'image/png';
        }[];
      };
      space: {
        spaceId: number;
        private: boolean;
      };
      spaceTabId: number;
      spaceTabTitle: string;
      documentId: number;
      accountId: number;
      projectId: number;
      commentThreadId: number;
      statusUpdate: {
        statusUpdateId: number;
      };
    };
  };
};

export type WebsocketCKDocumentConversationContext = {
  commentContext: {
    id: number;
    type: WebSocketConversationCommentContextType.Document;
    parentType: WebSocketConversationCommentContextType.SpaceTab;
    associatedDocument: {
      accountId: number;
      commentThreadId: number;
      documentId: number;
      projectId: number;
      spaceTab: {
        space: {
          spaceId: number;
          private: boolean;
        };
        spaceTabId: number;
      };
      title: string;
    };
  };
};

export type WebsocketTaskConversationContext = {
  commentContext: {
    id: number;
    type: WebSocketConversationCommentContextType.Task;
    parentType: WebSocketConversationCommentContextType.Task;
    associatedTask: {
      private: boolean;
      taskName: string;
      commentThreadId: number;
      documentId: number;
      projectId: number;
      spaceTab: {
        space: {
          spaceId: number;
        };
        spaceTabId: number;
      };
    };
  };
};

export type WebsocketProjectConversationContext = {
  commentContext: {
    type: WebSocketConversationCommentContextType.ProjectConversation;
  };
};

export type WebsocketConversationMetaData =
  | WebsocketCKDocumentConversationContext
  | WebsocketExternalDocumentConversationContext
  | WebsocketStatusUpdateConversationContext
  | WebsocketTaskConversationContext
  | WebsocketProjectConversationContext;
export interface BaseUserMessage extends BaseComment {
  content: string;
}

export enum MessageType {
  UserMessage = 'USER_MESSAGE',
  ControlMessage = 'CONTROL_MESSAGE',
}

export interface UserMessage extends BaseUserMessage {
  messageType: MessageType.UserMessage;
  createdBy: number;
  multiReactionSummary?: {
    summaries: ReactionProps[];
  };
  lastSeenAt: moment.Moment;
  commentMeta?: {
    syncedWithExternalIntegration?: boolean;
    linkedAttachments?: FileAttachment[];
    linkedUsers?: User[];
    linkedTasks?: Task[];
    linkedDocuments?: Document[];
    linkedComments?: null; // TODO::
    externalUser?: ExternalUser;
    repliedTo?: BaseUserMessage & {
      linkedAttachments?: FileAttachment[];
    };
  };
  fromSameUser?: boolean;
}

export interface BaseControlMessage extends BaseComment {
  messageType: MessageType.ControlMessage;
  commentMeta?: {
    externalUser?: ExternalUser;
  };
}

export enum MessageSubTypes {
  ProjectConversationNew = 'PROJECT_CONVERSATION_NEW',
  ProjectConversationNewMember = 'PROJECT_CONVERSATION_NEW_MEMBER',
  ProjectFolderAddAttachment = 'PROJECT_FOLDER_ADD_ATTACHMENT',
  ProjectFolderRemoveAttachment = 'PROJECT_FOLDER_REMOVE_ATTACHMENT',
  TaskNew = 'TASK_NEW',
  BulkTasksNew = 'TASK_BULK_NEW',
  TaskUpdateAttachment = 'TASK_UPDATE_ATTACHMENT',
  TaskUpdateList = 'TASK_UPDATE_LIST',
  TaskUpdateApproval = 'TASK_UPDATE_APPROVAL',
  TaskUpdateFollowers = 'TASK_UPDATE_FOLLOWERS',
  TaskUpdateDependentTask = 'TASK_UPDATE_DEPENDENT_TASK',
  TaskUpdateAssignee = 'TASK_UPDATE_ASSIGNEE',
  TaskUpdateSubTasks = 'TASK_UPDATE_SUB_TASKS',
  TaskUpdateField = 'TASK_UPDATE_FIELD',
  TaskUpdatePhase = 'TASK_UPDATE_PHASE',
  TaskCsatAdded = 'TASK_CREATE_CSAT_RATING',
  AttachmentComment = 'ATTACHMENT_COMMENT',
  AttachmentCommentThread = 'ATTACHMENT_COMMENT_THREAD',
  AttachmentCommentThreadResolve = 'ATTACHMENT_COMMENT_RESOLVE',
  AttachmentVersion = 'ATTACHMENT_VERSION',
  ResourceRequestSetAssignee = 'RESOURCE_REQUEST_SET_ASSIGNEE',
  ResourceRequestUpdateAssignee = 'RESOURCE_REQUEST_UPDATE_ASSIGNEE',
  ResourceRequestUpdateStatus = 'RESOURCE_REQUEST_UPDATE_STATUS',
  ResourceRequestCreate = 'RESOURCE_REQUEST_CREATE',
}

export interface ProjectConversationNew extends BaseControlMessage {
  messageSubType: MessageSubTypes.ProjectConversationNew;
  meta: {
    changeType: 'ADDED';
  };
}

export interface ProjectConversationNewMember extends BaseControlMessage {
  commentContext: {
    id: number;
    associatedProjectConversation: {
      conversationId: number;
      conversationName: string;
    };
  };
  messageSubType: MessageSubTypes.ProjectConversationNewMember;
  meta: {
    changeType: 'ADDED';
    users: User[];
  };
}

interface TaskNew extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskNew;
  meta: {
    changeType: 'ADDED';
  };
}

interface BulkTasksNew extends BaseControlMessage {
  messageSubType: MessageSubTypes.BulkTasksNew;
  meta: {
    changeType: 'ADDED';
    createdByUserName: string;
    taskIds: Array<number>;
    totalTasks: number;
  };
}

interface TaskUpdateAttachment extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateAttachment;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    attachmentId: number;
    attachmentName: string;
  };
}

interface ProjectFolderAddAttachment extends BaseControlMessage {
  messageSubType: MessageSubTypes.ProjectFolderAddAttachment;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    attachmentId: number;
    attachmentName: string;
  };
}

interface TaskUpdateList extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateList;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    changedValue: string;
    listName: string;
    fieldLabel: string;
  };
}

interface TaskUpdateApproval extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateApproval;
  meta: {
    changeType: 'ADDED' | 'UPDATED' | 'REMOVED';
    currentStatus: 'APPROVAL_REQUESTED' | 'CHANGE_REQUESTED';
    requestedByUserName: string;
    requestedToUsers?: string[];
  };
}

interface TaskUpdateFollower extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateFollowers;
  meta: {
    fieldLabel: string;
    changeType: 'ADDED' | 'REMOVED';
    changedObject: {
      userName?: string;
      teamName?: string;
    };
  };
}

interface TaskUpdateDependentTask extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateDependentTask;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    changedByUserName: string;
    changedObject: Task;
  };
}

interface TaskUpdateAssignee extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateAssignee;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    fieldLabel: string;
    changedObject: {
      userName?: string;
      teamName?: string;
    };
  };
}

interface TaskUpdateSubtask extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateSubTasks;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    changedByUserName: string;
    changedObject: Task;
  };
}

interface TaskCreateCsatRating extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskCsatAdded;
  meta: {
    changeType: 'ADDED' | 'REMOVED';
    fieldLabel: string;
    changedObject?: {
      firstName: string;
      lastName: string;
    };
  };
}
interface ResourceRequestSetAssignee extends BaseControlMessage {
  messageSubType: MessageSubTypes.ResourceRequestSetAssignee;
  meta: {
    changeType: 'ADDED';
    fieldLabel: string;
    fieldName: string;
    newValue: number;
    changedObject: {
      firstName: string;
      lastName: string;
    };
  };
  commentContext: {
    id: number;
    type: WebSocketConversationCommentContextType.ResourceRequest;
    associatedResourceRequest: {
      resourceRequestMappingId: number;
      projectId: number;
      accountId: number;
      commentThreadId: number;
      deleted: boolean;
    };
  };
}

interface ResourceRequestUpdateAssignee extends BaseControlMessage {
  messageSubType: MessageSubTypes.ResourceRequestUpdateAssignee;
  meta: {
    changeType: 'UPDATED';
    fieldLabel: string;
    fieldName: string;
    newValue: number;
  };
  commentContext: {
    id: number;
    type: WebSocketConversationCommentContextType.ResourceRequest;
    associatedResourceRequest: {
      resourceRequestMappingId: number;
      projectId: number;
      accountId: number;
      commentThreadId: number;
      deleted: boolean;
    };
  };
}

interface ResourceRequestUpdateStatus extends BaseControlMessage {
  messageSubType: MessageSubTypes.ResourceRequestUpdateStatus;
  meta: {
    changeType: 'UPDATED';
    fieldLabel: string;
    newValue: string;
    oldValue: string;
  };
  commentContext: {
    id: number;
    type: WebSocketConversationCommentContextType.ResourceRequest;
    associatedResourceRequest: {
      resourceRequestMappingId: number;
      projectId: number;
      accountId: number;
      commentThreadId: number;
      deleted: boolean;
    };
  };
}

interface ResourceRequestCreate extends BaseControlMessage {
  messageSubType: MessageSubTypes.ResourceRequestCreate;
  meta: {
    changeType: 'ADDED';
    fieldLabel: string;
    newValue: string;
  };
  commentContext: {
    id: number;
    type: WebSocketConversationCommentContextType.ResourceRequest;
    associatedResourceRequest: {
      resourceRequestMappingId: number;
      projectId: number;
      accountId: number;
      commentThreadId: number;
      deleted: boolean;
    };
  };
}

type BaseFieldControlMessageMeta = {
  changeType: 'ADDED' | 'UPDATED' | 'REMOVED';
  fieldId: number;
  fieldName: string;
  fieldType: 'SINGLE_SELECT' | 'MULTI_SELECT';
  customField: boolean;
  fieldLabel: string;
  optionsAvailable?: boolean;
};
export type DateFieldControlMessageMeta = BaseFieldControlMessageMeta & {
  fieldDataType: 'DATE';
  fieldType: 'SINGLE_SELECT';
  oldValue?: number;
  newValue?: number;
};

export type TextFieldControlMessageMeta = BaseFieldControlMessageMeta & {
  customField: false;
  fieldDataType: 'TEXT';
  fieldType: 'SINGLE_SELECT';
  oldValue?: string;
  newValue?: string;
};
export type CustomTextFieldControlMessageMeta = BaseFieldControlMessageMeta & {
  customField: true;
  fieldDataType: 'TEXT';
  fieldType: 'SINGLE_SELECT';
  oldValue?: FieldOption<string>;
  newValue?: FieldOption<string>;
};
export type MultiLineTextFieldControlMessageMeta =
  BaseFieldControlMessageMeta & {
    customField: true;
    fieldDataType: 'MULTI_LINE_TEXT';
    fieldType: 'SINGLE_SELECT';
    oldValue?: string;
    newValue?: string;
  };

export type MultiSelectTextFieldControlMessageMeta =
  BaseFieldControlMessageMeta & {
    fieldDataType: 'TEXT';
    fieldType: 'MULTI_SELECT';
    customField: true;
    oldValue?: FieldOption<string>[];
    newValue?: FieldOption<string>[];
  };
export type NumberFieldControlMessageMeta = BaseFieldControlMessageMeta & {
  fieldDataType: 'NUMBER';
  fieldType: 'SINGLE_SELECT';
  oldValue?: number;
  newValue?: number;
};

export type NumberFieldSingleSelectControlMessageMeta =
  BaseFieldControlMessageMeta & {
    fieldDataType: 'NUMBER';
    fieldType: 'SINGLE_SELECT';
    oldValue?: FieldOption<string>[];
    newValue?: FieldOption<string>[];
  };

export type NumberFieldMultiSelectControlMessageMeta =
  BaseFieldControlMessageMeta & {
    fieldDataType: 'NUMBER';
    fieldType: 'MULTI_SELECT';
    oldValue?: FieldOption<string>[];
    newValue?: FieldOption<string>[];
  };

export type BooleanFieldControlMessageMeta = BaseFieldControlMessageMeta & {
  fieldDataType: 'BOOLEAN';
  fieldType: 'SINGLE_SELECT';
  oldValue?: boolean;
  newValue?: boolean;
};
export type UserFieldControlMessageMeta = BaseFieldControlMessageMeta & {
  fieldDataType: 'USER';
  fieldType: 'SINGLE_SELECT' | 'MULTI_SELECT';
  oldValue?: User;
  newValue?: User;
  oldListObject?: User[];
  newListObject?: User[];
};
export type MilestoneControlMessageMeta = BaseFieldControlMessageMeta & {
  fieldDataType: 'TASK_TYPE';
  changeType: 'UPDATED';
  changedByUserName: string;
  oldValue?: TaskType;
  newValue: TaskType;
};

export type AttachmentCommentMeta = BaseControlMessage & {
  changeType: 'ADDED';
  commentId: number;
  createdBy: number;
  commentThreadId: number;
  createdAt: number;
  messageType: string;
  fieldDataType: 'ATTACHMENT';
  messageSubType:
    | MessageSubTypes.AttachmentComment
    | MessageSubTypes.AttachmentCommentThread
    | MessageSubTypes.AttachmentCommentThreadResolve
    | MessageSubTypes.AttachmentVersion;
  meta: {
    changedByUserId: number;
    changedByUserName: string;
    changeType: string;
    systemChange: boolean;
    attachment: FileAttachment;
    comment: Comment$;
  };
  user: User;
  auditLogId: number;
  commentGuid: string;
  traceId: string;
};

export type TaskUpdateFieldMeta =
  | DateFieldControlMessageMeta
  | TextFieldControlMessageMeta
  | CustomTextFieldControlMessageMeta
  | MultiLineTextFieldControlMessageMeta
  | NumberFieldControlMessageMeta
  | BooleanFieldControlMessageMeta
  | UserFieldControlMessageMeta
  | MultiSelectTextFieldControlMessageMeta
  | MilestoneControlMessageMeta
  | NumberFieldSingleSelectControlMessageMeta
  | NumberFieldMultiSelectControlMessageMeta;

interface TaskUpdateField extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdateField;
  meta: TaskUpdateFieldMeta;
}
interface TaskUpdatePhaseMeta extends BaseFieldControlMessageMeta {
  fieldDataType: 'NUMBER';
  fieldType: 'SINGLE_SELECT';
  oldValue?: FieldOption<number>;
  newValue?: FieldOption<number>;
}
interface TaskUpdatePhase extends BaseControlMessage {
  messageSubType: MessageSubTypes.TaskUpdatePhase;
  meta: TaskUpdatePhaseMeta;
}

export type ControlMessage =
  | TaskNew
  | BulkTasksNew
  | TaskUpdateAttachment
  | TaskUpdateList
  | TaskUpdateField
  | TaskUpdateApproval
  | TaskUpdateDependentTask
  | TaskUpdateSubtask
  | TaskUpdateFollower
  | TaskUpdateAssignee
  | TaskCreateCsatRating
  | ProjectConversationNew
  | ProjectConversationNewMember
  | TaskUpdatePhase
  | AttachmentCommentMeta
  | ProjectFolderAddAttachment
  | ResourceRequestSetAssignee
  | ResourceRequestUpdateAssignee
  | ResourceRequestUpdateStatus
  | ResourceRequestCreate;

export type Comment = ControlMessage | UserMessage;

export type ReadReceipt = {
  entityId: number;
  entityType: ProjectGlobalConversation['contextType'];
  members: { lastSeenCommentCreatedAt: number; user: { userId: number } }[];
};
